import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone } from 'react-icons/fi';
import { useGrowthRemindersReport } from '../../hooks/useReports';
import {
    EmptyState,
    SkeletonTableRow,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { growthSeverity } from '../../utils/growthStatus';
import { telLink } from '../../utils/format';
import { formatDateDisplay } from '../../utils/dates';
import { buildPdfReport } from '../../utils/pdfReport';
// Shared `uv-` table chrome with UpcomingVaccines — keeps the visual
// story consistent across Reminders tabs. ponytail: move to a neutral
// RemindersTab.css when a third tab reuses these styles.
import './UpcomingVaccines.css';

// Per-row follow-up action for the growth alert queue. Mirrors the
// pattern used by UpcomingVaccines so the two tabs feel consistent;
// storage key is namespaced to growth so the two dropdowns don't
// collide. ponytail: per-browser localStorage, no multi-device sync —
// upgrade when backend contact-tracking lands.
const ACTION_OPTIONS = [
    { key: 'pending', label: 'Pending' },
    { key: 'mother-contacted', label: 'Mother contacted' },
    { key: 'unreachable', label: 'Unreachable' },
    { key: 'referred-moh', label: 'Referred (MOH)' },
];

const ACTION_STORAGE_KEY = 'phm:growth-actions:v1';

// Renders one child row. Shared by the active queue (top) and the
// "referred to MOH" container (bottom) so the two tables can't drift
// in column shape or formatting. Extracted to keep the JSX above the
// early returns (loading / error) readable.
//
// When the parent groups rows by child (one child → many growth
// records), only the first row of the group renders the child-info
// columns (reg / name / phone) and uses rowSpan so they don't repeat
// per record. `isFirstInGroup` / `groupSize` default to a 1-row
// group so this stays a drop-in for un-grouped callers.
const GrowthRow = ({ row, actionKey, onActionChange, isFirstInGroup = true, groupSize = 1 }) => {
    // A child can surface with multiple concurrent concerns (e.g.
    // Severely Underweight from weight-for-age AND Wasting from
    // weight-for-length). Render every pill so the PHM sees the full
    // picture; falls back to a single ``row.status`` pill when the
    // array isn't present (older BE payloads).
    const pills = (row.statuses && row.statuses.length > 0)
        ? row.statuses
        : (row.status ? [row.status] : []);
    return (
        <tr>
            {isFirstInGroup && (
                <>
                    <td className="uv-childno" rowSpan={groupSize}>{row.childNo || '-'}</td>
                    <td className="fw-bold" rowSpan={groupSize}>
                        {row.childId ? (
                            <Link
                                to={`/growth/${row.childId}`}
                                className="uv-link"
                                title="Open growth record"
                            >
                                {row.childName || '-'}
                            </Link>
                        ) : (
                            row.childName || '-'
                        )}
                    </td>
                </>
            )}
            <td>
                <div className="uv-status-group" title={`Measured ${row.recordedOn || 'unknown date'}: ${row.weightKg ?? '-'} kg`}>
                    {pills.map((label) => {
                        const severity = growthSeverity(label);
                        return (
                            <span
                                key={label}
                                className={`uv-status uv-status--${severity}`}
                            >
                                {label}
                            </span>
                        );
                    })}
                    {pills.length === 0 && <span className="uv-status uv-status--neutral">-</span>}
                </div>
            </td>
            <td className="uv-recorded-on">{formatDateDisplay(row.recordedOn) || '-'}</td>
            <td>
                <select
                    className={`uv-action uv-action--${actionKey}`}
                    value={actionKey}
                    onChange={(e) => onActionChange(e.target.value)}
                    aria-label="Follow-up action"
                >
                    {ACTION_OPTIONS.map((opt) => (
                        <option key={opt.key} value={opt.key}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </td>
            {isFirstInGroup && (
                <td rowSpan={groupSize}>
                    {row.contactNumber ? (
                        <a
                            href={telLink(row.contactNumber)}
                            className="uv-call"
                            title="Call caregiver"
                        >
                            <FiPhone size={13} />
                            {row.contactNumber}
                        </a>
                    ) : (
                        <span className="uv-call uv-call--muted">—</span>
                    )}
                </td>
            )}
        </tr>
    );
};

const loadStoredActions = () => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(ACTION_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

/**
 * Growth tab — every active child whose **most recent** growth
 * classification is NOT "Normal". Each row surfaces every concurrent
 * concern the latest measurement triggered (e.g. Severely Underweight
 * from weight-for-age AND Wasting from weight-for-length), sorted by
 * clinical severity so the red badges float to the top. Children
 * whose latest measurement lands in the healthy range are hidden;
 * children with no growth records at all are also hidden (they
 * haven't been screened yet — separate outreach concern).
 *
 * Renders the same chrome as the Upcoming Vaccinations tab so the two
 * feel like siblings.
 */
const Growth = ({ onExportChange }) => {
    const { data, loading, error } = useGrowthRemindersReport();
    const toast = useToast();
    const rows = useMemo(() => data?.rows || [], [data]);

    // Per-row follow-up action. Persisted to localStorage so the
    // choice survives route changes and reloads; key is the same
    // composite used for React keys so it stays stable across
    // re-fetches. Syncs on every change — quota errors are swallowed.
    const [actions, setActions] = useState(loadStoredActions);
    const setAction = (key, value) =>
        setActions((prev) => {
            const next = { ...prev, [key]: value };
            try {
                window.localStorage.setItem(ACTION_STORAGE_KEY, JSON.stringify(next));
            } catch {
                /* private mode / quota — fall back to in-memory only */
            }
            return next;
        });

    // Split rows by follow-up action: anything tagged "referred-moh"
    // drops into the bottom container so the active queue stays focused
    // on the children the PHM can still action. Re-keyed by rowKey so
    // the two tables stay in sync with localStorage. Declared BEFORE
    // the loading/error early returns so the hook count is stable
    // across renders (rules of hooks).
    const splitRows = useMemo(() => {
        const top = [];
        const referred = [];
        rows.forEach((r) => {
            const rowKey = `${r.childId}-${r.recordId}`;
            const actionKey = actions[rowKey] || 'pending';
            (actionKey === 'referred-moh' ? referred : top).push({ row: r, rowKey, actionKey });
        });
        return { top, referred };
    }, [rows, actions]);

    // Group each list by child so the tables can rowSpan the
    // child-info columns (reg / name / phone). A child with multiple
    // growth records in the alert list appears once on the left and
    // the records fan out under it. Same pattern as Reminders →
    // Milestones and → Vaccinations.
    const groupByChild = (items) => {
        const map = new Map();
        for (const it of items) {
            if (!map.has(it.row.childId)) map.set(it.row.childId, []);
            map.get(it.row.childId).push(it);
        }
        return Array.from(map.values()).map((items) => ({
            childId: items[0].row.childId,
            items,
        }));
    };
    const groupedTop = useMemo(() => groupByChild(splitRows.top), [splitRows.top]);
    const groupedReferred = useMemo(() => groupByChild(splitRows.referred), [splitRows.referred]);

    // PDF export — mirrors the visible state: a status summary, the
    // active queue, and a "Referred to MOH" section when the bottom
    // container is non-empty. Each row carries the same contact-outcome
    // label the dropdown shows on screen, so the export reflects the
    // PHM's in-progress work. Same `onExportChange` pattern as
    // UpcomingVaccines so the parent's Export PDF button stays
    // contextual to the active tab.
    const exportPDF = useCallback(() => {
        const stamp = new Date().toISOString().split('T')[0];
        const actionLabel = (key) =>
            (ACTION_OPTIONS.find((o) => o.key === key) || ACTION_OPTIONS[0]).label;
        // Each record can carry multiple concurrent concerns (e.g. both
        // Severely Underweight AND Wasting). Tally every status the
        // record reports so the summary reflects "how many records
        // surface this concern" — same counting rule as the table.
        const statusCounts = new Map();
        rows.forEach((r) => {
            const pills = (r.statuses && r.statuses.length > 0)
                ? r.statuses
                : (r.status ? [r.status] : []);
            pills.forEach((p) => statusCounts.set(p, (statusCounts.get(p) || 0) + 1));
        });
        const summaryBody = Array.from(statusCounts.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([status, count]) => [status, count]);
        const flatRow = ({ row, actionKey }) => {
            const pills = (row.statuses && row.statuses.length > 0)
                ? row.statuses
                : (row.status ? [row.status] : []);
            return [
                row.childNo || '-',
                row.childName || '-',
                pills.join(', ') || '-',
                formatDateDisplay(row.recordedOn) || '-',
                actionLabel(actionKey),
                row.contactNumber || '-',
            ];
        };
        const sections = [
            {
                heading: 'Status Summary',
                head: ['Growth Status', 'Records'],
                body: summaryBody,
                accent: [245, 158, 11], // amber-500
            },
            {
                heading: 'Active Queue',
                head: ['Reg No', 'Child Name', 'Status', 'Last Weighted', 'Action', "Mother's Phone"],
                body: splitRows.top.map(flatRow),
                accent: [217, 119, 6], // amber-600
            },
        ];
        if (splitRows.referred.length > 0) {
            sections.push({
                heading: 'Referred to MOH',
                head: ['Reg No', 'Child Name', 'Status', 'Last Weighted', 'Action', "Mother's Phone"],
                body: splitRows.referred.map(flatRow),
                accent: [217, 119, 6],
            });
        }
        buildPdfReport({
            title: 'Growth Reminders Report',
            filters: [],
            sections,
            fileName: `Growth_Reminders_${stamp}.pdf`,
            toast,
        });
    }, [rows, splitRows, toast]);

    useEffect(() => {
        onExportChange?.({ exportPDF, canExport: rows.length > 0 });
    }, [onExportChange, exportPDF, rows.length]);

    if (loading) {
        return (
            <div className="uv-body">
                <section className="uv-card" aria-label="Growth reminders list">
                    <div className="uv-table-wrap">
                        <table className="uv-table">
                            <thead>
                                <tr>
                                    <th>Reg No</th>
                                    <th>Child Name</th>
                                    <th>Status</th>
                                    <th>Last weighted</th>
                                    <th>Action</th>
                                    <th>Mother's Phone</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <SkeletonTableRow key={i} cols={6} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        );
    }

    if (error) {
        return (
            <div className="uv-body">
                <EmptyState
                    illustration="error"
                    title="Couldn't load growth alerts"
                    description={error}
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => window.location.reload()}
                        >
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="uv-body">
            <section className="uv-card" aria-label="Growth reminders list">
                <div className="uv-table-wrap">
                    <table className="uv-table">
                        <thead>
                            <tr>
                                <th>Reg No</th>
                                <th>Child Name</th>
                                <th>Status</th>
                                <th>Last weighted</th>
                                <th>Action</th>
                                <th>Mother's Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedTop.map((group) =>
                                group.items.map(({ row, rowKey, actionKey }, idx) => (
                                    <GrowthRow
                                        key={rowKey}
                                        row={row}
                                        actionKey={actionKey}
                                        onActionChange={(v) => setAction(rowKey, v)}
                                        isFirstInGroup={idx === 0}
                                        groupSize={group.items.length}
                                    />
                                )),
                            )}
                            {groupedTop.length === 0 && rows.length > 0 && (
                                <tr>
                                    <td colSpan={6} className="uv-empty-cell">
                                        <EmptyState
                                            illustration="check"
                                            compact
                                            title="No children pending follow-up"
                                            description="Every growth alert has been referred to the MOH or resolved — see the container below."
                                        />
                                    </td>
                                </tr>
                            )}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="uv-empty-cell">
                                        <EmptyState
                                            illustration="check"
                                            compact
                                            title="No children currently trigger a WHO growth alert"
                                            description="Every active child's most recent weight measurement is within the normal range — nothing to follow up on."
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {splitRows.referred.length > 0 && (
                <section
                    className="uv-card uv-card--referred"
                    aria-label="Children referred to MOH"
                >
                    <header className="uv-card__header">
                        <h3>Child Referred to MOH</h3>
                        <span className="uv-card__subtitle">
                            {groupedReferred.length} child{groupedReferred.length === 1 ? '' : 'ren'} pending MOH review
                        </span>
                    </header>
                    <div className="uv-table-wrap">
                        <table className="uv-table">
                            <thead>
                                <tr>
                                    <th>Reg No</th>
                                    <th>Child Name</th>
                                    <th>Status</th>
                                    <th>Last weighted</th>
                                    <th>Action</th>
                                    <th>Mother's Phone</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedReferred.map((group) =>
                                    group.items.map(({ row, rowKey, actionKey }, idx) => (
                                        <GrowthRow
                                            key={rowKey}
                                            row={row}
                                            actionKey={actionKey}
                                            onActionChange={(v) => setAction(rowKey, v)}
                                            isFirstInGroup={idx === 0}
                                            groupSize={group.items.length}
                                        />
                                    )),
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
};

export default Growth;


